import { CalendarFilled } from "@ant-design/icons";
import { Button, Col, Form, Modal, notification, QRCode, Row } from "antd";
import React, { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import ProfileImage from "../../../assets/profile-img.svg";
import { ModalInput } from "../../../components/AntCostomComponents/Inputs";
import ProgressCard from "../../../components/ProgressCard";
import { UserContext } from "../../../context/UserContext";
import { deletePatient, getPatientAdherence, updatePatient } from "../../../utils/api-lists/patient.api";
import { sendPatientMobileVerification } from "../../../utils/api-lists/verification.api";
import { formatUtcToLocal } from "../../../utils/date.util";


const PatientInfoHeader = React.forwardRef(({patient, date, onUpdate}, ref) => {
    const {first_name, last_name, email, phone} = patient;
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [openInMobileModal, setOpenInMobileModal] = useState(false);
    const [adherence, setAdherence] = useState();
    const [period, setPeriod] = useState("30d");
    const {user} = useContext(UserContext);
    const currentOrigin = window.location.origin.includes('localhost') ? 'https://www.glacies.ai' : window.location.origin;
    const {t} = useTranslation("common");
    const [form] = Form.useForm();
    const navitage = useNavigate();
    const {patientId} = useParams();

    const fetchAdherence = async () => {
        try {
            const response = await getPatientAdherence(patientId, period)
            setAdherence(response);
        }catch (e) {
            console.error("Error fetching adherence:", e);
        }
    }

useEffect(() => {
  fetchAdherence();
}, [period]);

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        form.resetFields();
        setIsModalVisible(false);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            await updatePatient(
                patient?.id,
                values.first_name,
                values.last_name,
                values.email,
                values.phone
            );

            notification.success({
                message: t("success"),
                description: t("patient_update_success"),
                placement: "top",
                duration: 3,
            });

            setIsModalVisible(false);
            onUpdate();
        } catch (err) {
            console.log("Validation error:", err);
            notification.error({
                message: t("error"),
                description: t("patient_update_failed"),
                placement: "top",
                duration: 3,
            });
        }
    };
    const sendPatientMobile = () => {

        toast.promise(
            sendPatientMobileVerification(patient.email), {
                loading: t("sending_mobile_verification"),
                success: t("mobile_verification_sent"),
                error: (e) => `${t("mobile_verification_error")}: ${e.response}`,
            }
        ).then(() => {
            openMobileModal();
        }).catch((e) => {
            console.error("Error sending mobile verification:", e);
        });
    }
    const handleDelete = async () => {
        try {
            await deletePatient(patient?.id);
            setIsDeleteModalVisible(false);
            onUpdate();
            navitage("/patient-list");

            notification.info({
                message: t("info"),
                description: t("patient_delete_success"),
                placement: "top",
                duration: 3,
            });

        } catch (err) {
            console.log("Error deleting patient:", err);
            notification.error({
                message: t("error"),
                description: t("patient_delete_failed"),
                placement: "top",
                duration: 3,
            });
        }
    }

    const openMobileModal = () => {
        setOpenInMobileModal(!openInMobileModal);
    }

    const handleDeleteCancel = () => {
        setIsDeleteModalVisible(false);
    };

    // Expose functions to parent component via ref
    React.useImperativeHandle(ref, () => ({
        showEditModal: () => setIsModalVisible(true),
        showDeleteModal: () => setIsDeleteModalVisible(true)
    }));

    return (
        <div className="relative p-6 bg-[#2B2238] rounded-xl w-full profile-header">
            {/* Main Content */}
            <div className="flex flex-col sm:flex-row justify-between items-start gap-6">
                {/* Left Side - Profile Info */}
                <div className="flex flex-col sm:flex-row items-start gap-4">
                    <div className="relative">
                        <img
                            src={ProfileImage}
                            alt="Profile"
                            className="w-24 h-24 rounded-full"
                        />
                        {/* Green status indicator */}
                        <div
                            className="absolute bottom-2 right-2 w-4 h-4 bg-green-500 rounded-full border-2 border-[#2B2238]"></div>
                    </div>
                    <div className="flex flex-col">
                        <h2 className="text-2xl font-semibold text-white mb-4">
                            {`${first_name} ${last_name}`}
                        </h2>
                        <div className="flex flex-col sm:flex-row gap-6">
                            <div>
                                <p className="text-gray-400 text-sm mb-1">Phone</p>
                                <p className="text-white">{phone || '+1 (555) 987-6543'}</p>
                            </div>
                            <div>
                                <p className="text-gray-400 text-sm mb-1">Email</p>
                                <p className="text-white">{email}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side - Last Visit and Request Button */}
                <div className="flex flex-col items-end justify-between min-h-[100px]">
                    <div>
                        {date && (
                            <div className="flex items-center gap-2 text-gray-400">
                                <CalendarFilled className="text-gray-400 text-base"/>
                                <span className="text-gray-400 font-medium">{`Last visit: ${formatUtcToLocal(date)}`}</span>
                            </div>
                        )}
                    </div>

                  <div className="flex flex-row items-end justify-between mt-3">
                    <ProgressCard percentage={adherence?.adherence_percentage || 0} period={period} onPeriodChange={setPeriod}  />
                    </div>
                </div>
            </div>
            {/* Delete Modal */}
            <Modal
                title={t("delete_patient")}
                open={isDeleteModalVisible}
                onCancel={handleDeleteCancel}
                footer={null}
                width={700}
                className="custom-dark-modal"

            >
                <h3>{t("delete_patient_confirmation")}</h3>
                <div className="mt-4 flex justify-end gap-2">
                    <Button
                        type="primary"
                        onClick={handleDeleteCancel}
                        style={{backgroundColor: "red", borderColor: "red"}}
                    >
                        {t("cancel")}
                    </Button>

                    <Button
                        type="primary"
                        onClick={handleDelete}
                        style={{backgroundColor: "#8e44ad", borderColor: "#8e44ad"}}
                    >
                        {t("delete")}
                    </Button>
                </div>
            </Modal>

            {/* Edit Modal */}
            <Modal
                title={t("edit_patient")}
                open={isModalVisible}
                onCancel={handleCancel}
                footer={null}
                width={700}
                className="custom-dark-modal"
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        first_name,
                        last_name,
                        email,
                        phone,
                    }}
                >
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label={t("first_name")}
                                name="first_name"
                                rules={[{
                                    required: true,
                                    message: t("first_name_required"),
                                }, {type: "string", message: t("must_be_string")}]}
                            >
                                <ModalInput/>
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                            <Form.Item
                                label={t("last_name")}
                                name="last_name"
                                rules={[{required: true, message: t("last_name_required")},
                                    {type: "string", message: t("must_be_string")}]}
                            >
                                <ModalInput/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label={t("phone_number")}
                                name="phone"
                                rules={[{
                                    required: true,
                                    message: t("phone_number_required"),
                                },
                                    {
                                        pattern: /^[0-9]+$/,
                                        message: t("phone_must_number"),
                                    },
                                ]}
                            >
                                <ModalInput/>
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                            <Form.Item
                                label={t("email")}
                                name="email"
                                validateTrigger="onBlur"
                                rules={[
                                    {
                                        type: "email",
                                        message: t("enter_valid_email"),
                                    },
                                ]}
                            >
                                <ModalInput/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <div className="mt-4 flex justify-end">
                        <Button
                            type="primary"
                            onClick={handleSubmit}
                            style={{backgroundColor: "#8e44ad", borderColor: "#8e44ad"}}
                        >
                            {t("update")}
                        </Button>
                    </div>
                </Form>
            </Modal>

            <Modal
                title={t("open_in_mobile")}
                open={openInMobileModal}
                footer={null}
                onCancel={openMobileModal}
                width={{
                    lg: "1060px",
                    xl: "1060px",
                    xxl: "1060px",
                }}
                // styles={{body: {height: '90%'}}}
                destroyOnClose={true}
            >
                <div style={{
                    maxHeight: "80vh",
                    minHeight: "400px",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                }}>
                    <QRCode
                        errorLevel="L"
                        value={`${currentOrigin}/deeplink?action=camera&patientId=${patientId}`}
                        size={300}
                    />
                    <div className="flex flex-row gap-3 justify-center items-center mt-4">
                        <a href="https://apps.apple.com/app/id1551353775" className="storeLink">
                            <img
                                style={{
                                    width: 300,
                                    padding: 30
                                }}
                                src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us"
                                alt="Download on the App Store"/>
                        </a>
                        <a
                            href='https://play.google.com/store/apps/details?id=com.stagescycling.stages'
                            className="storeLink">
                            <img alt='Get it on Google Play'
                                 style={{
                                     width: 300,
                                 }}
                                 src='https://play.google.com/intl/en_us/badges/images/generic/en_badge_web_generic.png'/>
                        </a>
                    </div>
                </div>
            </Modal>
        </div>
    );
});

export default PatientInfoHeader;
